import axios from "axios";

import {
  ReferralCode,
  ReferralCodeFilters,
  ReferralCodePagination,
  ReferralCodePayload,
  ReferralUserOption,
} from "../../../pages/Referrals/types";

const API_URL = process.env.REACT_APP_API_URL;
const REFERRAL_CODES_URL = `${API_URL}/referral-codes`;

const authHeaders = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

const appendFilters = (filters: ReferralCodeFilters) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });

  return params.toString();
};

export const fetchReferralCodes = async (
  token: string,
  filters: ReferralCodeFilters,
): Promise<ReferralCodePagination> => {
  const query = appendFilters(filters);
  const response = await axios.get(
    `${REFERRAL_CODES_URL}${query ? `?${query}` : ""}`,
    authHeaders(token),
  );
  const paginator = response.data?.data ?? response.data;

  return {
    data: Array.isArray(paginator?.data) ? paginator.data : [],
    current_page: Number(paginator?.current_page ?? 1),
    last_page: Number(paginator?.last_page ?? 1),
    per_page: Number(paginator?.per_page ?? filters.per_page ?? 15),
    total: Number(paginator?.total ?? 0),
  };
};

const unwrapReferralCode = (payload: any): ReferralCode =>
  payload?.data ?? payload?.referral_code ?? payload;

export const createReferralCode = async (
  token: string,
  payload: ReferralCodePayload,
) => {
  const response = await axios.post(
    REFERRAL_CODES_URL,
    payload,
    authHeaders(token),
  );

  return unwrapReferralCode(response.data);
};

export const updateReferralCode = async (
  token: string,
  id: number,
  status: ReferralCodePayload["status"],
) => {
  const response = await axios.patch(
    `${REFERRAL_CODES_URL}/${id}`,
    { status },
    authHeaders(token),
  );

  return unwrapReferralCode(response.data);
};

export const searchReferralUsers = async (
  token: string,
  search: string,
): Promise<ReferralUserOption[]> => {
  const params = new URLSearchParams({
    page: "1",
    per_page: "30",
  });
  if (search.trim()) params.set("search", search.trim());

  const response = await axios.get(
    `${REFERRAL_CODES_URL}/available-users?${params.toString()}`,
    authHeaders(token),
  );
  const paginator = response.data?.data ?? response.data;

  return Array.isArray(paginator?.data) ? paginator.data : [];
};

export const getReferralApiError = (
  error: unknown,
  fallback: string,
): string => {
  if (!axios.isAxiosError(error)) return fallback;

  const data = error.response?.data;
  const validationMessage = data?.errors
    ? Object.values(data.errors).flat().find(Boolean)
    : null;

  return String(validationMessage ?? data?.message ?? fallback);
};
